import type {PlanId} from "./planCatalogue";

/**
 * Which two plans the comparison shows side by side where the four-plan table does not fit its frame
 * (kitecorp/transferify-ui#4018, owner decisions 2026-11-01): one plan per picker above the two columns.
 * Plain TypeScript, so the rules survive any rework of the table that renders them.
 */

/** The two pickers, in the order their columns stand. */
export type ComparisonPicker = "first" | "second";

export const COMPARISON_PICKERS: readonly ComparisonPicker[] = ["first", "second"];

/** The plan each picker shows. */
export type ComparedPlans = Readonly<Record<ComparisonPicker, PlanId>>;

/** What the comparison opens on: the two top plans, the most capable first, as the four-plan table orders them. */
const DEFAULT_PAIR: ComparedPlans = {first: "premium", second: "plus"};

/**
 * What the sign-up Terms line's "See what the free plan includes" link opens it on: the free plan beside the
 * cheapest paid one, the comparison a person deciding whether Free is enough wants.
 */
const FREE_PLAN_PAIR: ComparedPlans = {first: "basic", second: "free"};

/**
 * The pair a comparison opens on. The address's fragment names the free plan only on the surface that carries
 * that anchor (the `/pricing` page's `FREE_PLAN_ANCHOR_ID`); the pricing dialog carries none, so a fragment on
 * the page beneath it changes nothing.
 */
export function openingComparedPlans(hash: string, freePlanAnchorId: string | undefined): ComparedPlans {
	return freePlanAnchorId !== undefined && hash === `#${freePlanAnchorId}` ? FREE_PLAN_PAIR : DEFAULT_PAIR;
}

/**
 * The pair after a person picks `plan` in one picker. Picking the plan the other picker shows swaps the two,
 * so a plan is never compared with itself.
 *
 * `from` is the pair as it stood when this pick began (the picker took focus or was pressed). On Windows
 * and Linux the arrow keys change a closed select at every step, so a person stepping the first picker from
 * Premium down to Free passes Plus on the way: counted from the pair on screen, that step would swap Plus out of
 * the second picker, and the walk would end on Free and Premium instead of Free and Plus.
 */
export function chooseComparedPlan(from: ComparedPlans, picker: ComparisonPicker, plan: PlanId): ComparedPlans {
	const other: ComparisonPicker = picker === "first" ? "second" : "first";
	const otherPlan = from[other] === plan ? from[picker] : from[other];
	return picker === "first" ? {first: plan, second: otherPlan} : {first: otherPlan, second: plan};
}

/**
 * Whether the two-plan view leads with its Price row: not while a welcome run prices any plan (review of
 * kitecorp/transferify-ui#4020). A plan in the run carries a first-month amount and no monthly price, so the
 * row would read "Price" over empty cells; the four-plan table's header leaves its price line empty then too.
 */
export function comparisonShowsPrices(plans: readonly {welcome?: boolean}[]): boolean {
	return !plans.some((plan) => plan.welcome === true);
}
